import type { MigrationManifest, PersistConfig, PersistMigrate } from 'redux-persist';
import { createMigrate, getStoredState } from 'redux-persist';
import type { Storage, WebStorage } from 'redux-persist/es/types';
import createWebStorage from 'redux-persist/lib/storage/createWebStorage';
import getStorage from 'redux-persist/lib/storage/getStorage';

import type { FeatureRollout, YammerState } from '../state/types';

export { createMigrate };
export type { FeatureRollout, MigrationManifest, PersistConfig };

export interface GetPersistConfigProps {
  readonly key: string;
  readonly version?: number;
  readonly migrate?: PersistMigrate;
  readonly allowlist?: (keyof YammerState)[];
  readonly preserveEcsPersistedFields?: boolean;
}

// this provides a fallback noop storage option for non-browser environments, namely jest's node test environment
let noopStorage: WebStorage;
export const getNoopStorage = (): WebStorage => {
  if (noopStorage) {
    return noopStorage;
  }
  noopStorage = {
    getItem() {
      return Promise.resolve(null);
    },
    setItem() {
      return Promise.resolve();
    },
    removeItem() {
      return Promise.resolve();
    },
  };

  return noopStorage;
};

const ecsPersistedFieldNames = ['ecsConfig', 'ecsExperiments', 'ecsFeatureRollouts'] as const;

const isPersistedStateRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const parsePersistedState = (serializedState: string): Readonly<Record<string, unknown>> => {
  const persistedState: unknown = JSON.parse(serializedState);

  if (!isPersistedStateRecord(persistedState)) {
    throw new Error('Persisted state must be a serialized object.');
  }

  return persistedState;
};

const readPersistedState = (storage: Storage, key: string): Readonly<Record<string, unknown>> => {
  try {
    const serializedState: unknown = storage.getItem(key);

    if (typeof serializedState !== 'string') {
      return {};
    }

    const persistedState: unknown = JSON.parse(serializedState);

    return isPersistedStateRecord(persistedState) ? persistedState : {};
  } catch (error) {
    if (error instanceof SyntaxError) {
      return {};
    }

    throw error;
  }
};

const selectEcsPersistedFields = (
  persistedState: Readonly<Record<string, unknown>>
): Readonly<Record<string, unknown>> =>
  ecsPersistedFieldNames.reduce<Readonly<Record<string, unknown>>>((ecsFields, fieldName) => {
    const fieldValue = persistedState[fieldName];

    return fieldValue === undefined
      ? ecsFields
      : {
          ...ecsFields,
          [fieldName]: fieldValue,
        };
  }, {});

const createStoragePromise = <T>(operation: () => T): Promise<T> =>
  new Promise((resolve) => {
    resolve(operation());
  });

const preserveEcsPersistedFields = (storage: Storage): WebStorage => ({
  getItem: (key) =>
    createStoragePromise(() => {
      const serializedState: unknown = storage.getItem(key);

      return typeof serializedState === 'string' ? serializedState : null;
    }),
  removeItem: (key) =>
    createStoragePromise(() => {
      storage.removeItem(key);
    }),
  setItem: (key, serializedState) =>
    createStoragePromise(() => {
      storage.setItem(
        key,
        JSON.stringify({
          ...parsePersistedState(serializedState),
          ...selectEcsPersistedFields(readPersistedState(storage, key)),
        })
      );
    }),
});

type GetPersistConfig = <T extends YammerState>(props: GetPersistConfigProps) => PersistConfig<T>;
export const getPersistConfig: GetPersistConfig = ({
  key,
  version,
  migrate,
  allowlist = [],
  preserveEcsPersistedFields: shouldPreserveEcsPersistedFields = false,
}) => {
  const isBrowserEnvironment = typeof window !== 'undefined';
  let storage = getNoopStorage();

  if (isBrowserEnvironment) {
    storage = shouldPreserveEcsPersistedFields
      ? preserveEcsPersistedFields(getStorage('local'))
      : createWebStorage('local');
  }

  return {
    storage,
    key,
    version,
    migrate,
    whitelist: allowlist,
  };
};

export type GetPersistedState = (key: string) => Promise<Partial<YammerState> | undefined>;
export const getPersistedState: GetPersistedState = async (key) => getStoredState(getPersistConfig({ key }));
